import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Fills the `recently-viewed` section with the products stored in localStorage.
 *
 * The cards themselves are rendered by the `recently-viewed-render` sections
 * through the Section Rendering API, so they are styled by the theme and always
 * show live prices and availability.
 */
class RecentlyViewedProducts extends HTMLElement {
  /** @type {boolean} */
  #loading = false;

  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#load();
    },
    // Perf (FLOLAE 2026-08-24): the observer is only attached once the page has loaded,
    // so a wide margin means "fetch as soon as the page is done" instead of "fetch when
    // the shopper has already scrolled here and is waiting".
    { rootMargin: '0px 0px 1600px 0px' }
  );

  connectedCallback() {
    if (this.#productIds().length === 0) {
      this.#showEmptyState();
      return;
    }

    // A cached render of the exact same product list is reused straight away, which makes
    // the section instant while browsing from product to product.
    if (this.#renderFromCache()) return;

    this.#observeWhenIdle();
  }

  #observeWhenIdle = () => {
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      this.#intersectionObserver.observe(this);
    };
    const idle = () =>
      typeof requestIdleCallback === 'function' ? requestIdleCallback(start, { timeout: 800 }) : setTimeout(start, 200);

    if (document.readyState === 'complete') {
      idle();
      return;
    }

    window.addEventListener('load', idle, { once: true });
    // A single stalled image must never leave the section empty, so start anyway.
    setTimeout(idle, 2500);
  };

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
  }

  /**
   * The sessionStorage key for the current id list and layout.
   * @returns {string}
   */
  #cacheKey() {
    // FLOLAE_RV_CACHE_THEME 2026-09-16: the cached HTML carries the card stylesheet, so a
    // response rendered by a previous theme must not survive a theme publish.
    const themeId = window.Shopify?.theme?.id ?? 'theme';
    return `flo:rv:${themeId}:${this.dataset.renderSection}:${this.#productIds().join(',')}`;
  }

  /**
   * Renders a previously fetched response for this exact id list, if there is one.
   * @returns {boolean} Whether the section was filled from the cache.
   */
  #renderFromCache() {
    let html = null;

    try {
      html = sessionStorage.getItem(this.#cacheKey());
    } catch {
      // Private mode / storage disabled - just fetch as usual.
    }

    if (!html) return false;

    return this.#inject(html);
  }

  /**
   * The ids to show, newest first, without the product of the current page.
   * @returns {string[]}
   */
  #productIds() {
    const max = parseInt(this.dataset.maxProducts ?? '16', 10) || 16;
    const excludeId = this.dataset.excludeId;

    return RecentlyViewed.getProducts()
      .filter(Boolean)
      .map(String)
      .filter((id) => id !== String(excludeId))
      .slice(0, max);
  }

  async #load() {
    if (this.#loading) return;
    this.#loading = true;

    const renderSection = this.dataset.renderSection;
    const productIds = this.#productIds();

    if (!renderSection || productIds.length === 0) {
      this.#showEmptyState();
      return;
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('resources[type]', 'product');

    try {
      const html = await sectionRenderer.getSectionHTML(renderSection, false, url);

      if (!this.#inject(html)) {
        this.#showEmptyState();
        return;
      }

      try {
        sessionStorage.setItem(this.#cacheKey(), html);
      } catch {
        // Storage full or unavailable - the section still rendered, so carry on.
      }
    } catch (error) {
      console.error('Recently viewed products error:', error);
      this.#showEmptyState();
    }
  }

  /**
   * Puts a section response into the page.
   * @param {string} html - The `recently-viewed-render` response
   * @returns {boolean} Whether anything was rendered.
   */
  #inject(html) {
    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const incoming = fragment.querySelector('[data-recently-viewed-items]');
    const target = this.querySelector('[data-recently-viewed-items]');

    if (!target || !incoming || Number(incoming.dataset.count ?? 0) === 0) return false;

    // The card styles are subset per page, so they travel with the response.
    this.#injectStylesheet(fragment);

    const cards = document.importNode(incoming, true);
    target.replaceChildren(...cards.childNodes);
    target.dataset.count = cards.dataset.count ?? '0';
    this.dataset.state = 'loaded';

    return true;
  }

  /**
   * Copies `style[data-section-stylesheet]` from the response into this section.
   * @param {Document} fragment - The parsed response
   */
  #injectStylesheet(fragment) {
    const stylesheet = fragment.querySelector('style[data-section-stylesheet]');
    if (!stylesheet) return;

    const existing = this.querySelector('style[data-section-stylesheet]');

    if (existing) {
      existing.textContent = stylesheet.textContent;
    } else {
      this.prepend(document.importNode(stylesheet, true));
    }
  }

  #showEmptyState() {
    // Keep the section visible in the theme editor so it can still be styled.
    if (window.Shopify?.designMode) {
      this.dataset.emptyBehavior = 'message';
    }

    this.dataset.state = 'empty';
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
